// apps/web/src/features/clients/components/ClientEditModal.tsx
//
// PATCH /clients/:id — edits `name` / `comment`. Diff-against-server is
// done by RHF's `dirtyFields`; we send only changed slots so the
// same-value PATCH no-op stays on the server side.
//
// Unsaved-changes guard: all close paths (Modal onClose, Cancel, Escape,
// backdrop) are routed through the EditorModal wrapper guard. Baseline is the
// loaded client values (name + comment). Successful save calls `reset()`
// so RHF marks the form clean before onClose fires — no stale warning.
import { useId } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CLIENT_NAME_MAX_LENGTH,
  PatchClientInputSchema,
  type ClientDetail,
  type PatchClientInput,
} from '@magimp/shared';
import { usePatchClient } from '@magimp/shared-hooks';
import { Banner, Button, EditorModal, FormRow, Input, Textarea } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { useClientFieldError } from '../useClientFieldError.js';
import { useClientSubmitError } from '../useClientSubmitError.js';
import { useMobileFieldStyle, useTouchActionStyle } from '../useMobileFieldStyle.js';
import { EditorCancelButton } from './EditorCancelButton.js';

export interface ClientEditModalProps {
  client: ClientDetail;
  onClose: () => void;
}

type Form = { name: string; comment: string };

export function ClientEditModal({ client, onClose }: ClientEditModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const show = useToastStore((s) => s.show);
  const patch = usePatchClient(client.id);
  const submitError = useClientSubmitError();
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const formId = useId();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<Form>({
    // WHY: PatchClientInputSchema is nonEmptyPatch over a `.partial()` object
    // (refuses an empty PATCH at the wire level). The form holds string-only
    // local state; we narrow into the wire schema at submit time, sending
    // only the dirty slots. `name` may not be empty per the wire spec —
    // surface that as a field error rather than a server 422.
    resolver: zodResolver(PatchClientInputSchema as never),
    defaultValues: { name: client.name, comment: client.comment ?? '' },
  });

  const onSubmit = (values: Form) => {
    submitError.clear();
    const payload: PatchClientInput = {};
    if (dirtyFields.name) payload.name = values.name;
    if (dirtyFields.comment) payload.comment = values.comment === '' ? null : values.comment;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    patch.mutate(payload, {
      onSuccess: () => {
        // Reset the form so RHF marks it clean before onClose fires.
        // This prevents the guard from seeing stale dirty state on close.
        reset({ name: values.name, comment: values.comment });
        show({ tone: 'success', text: t('clients:toasts.saved') });
        onClose();
      },
      // A name collision belongs ON the name field, not in a toast that
      // disappears while the offending text is still in the form. The PATCH is
      // addressed at this client's id, so a missing verdict is retryable.
      onError: (err) =>
        submitError.report(
          err,
          (field, message) => {
            if (field === 'name') setError('name', { message }, { shouldFocus: true });
          },
          { idempotent: true },
        ),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('clients:modals.editTitle')}
      width={480}
      narrowFullWidth
      dirty={isDirty}
      busy={patch.isPending}
      onConfirmClose={onClose}
      footer={
        <>
          <EditorCancelButton
            onClose={onClose}
            label={t('common:actions.cancel')}
            style={actionStyle}
          />
          <Button
            type="submit"
            form={formId}
            kind="primary"
            style={actionStyle}
            disabled={patch.isPending}
            data-testid="client-edit-submit"
          >
            {t('common:actions.save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        {submitError.state.text !== null && (
          <div style={{ marginBottom: 'var(--s-3)' }}>
            <Banner tone="error">{submitError.state.text}</Banner>
          </div>
        )}
        <FormRow
          label={t('clients:fields.name')}
          error={fieldError(errors.name, { kind: 'text', max: CLIENT_NAME_MAX_LENGTH })}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.name}
                maxLength={CLIENT_NAME_MAX_LENGTH}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
        <FormRow
          label={t('clients:fields.comment')}
          error={fieldError(errors.comment, { kind: 'text' })}
        >
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <Textarea
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.comment}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
      </form>
    </EditorModal>
  );
}
